import { Navigate, useAction, useLocation, useSubmission } from '@solidjs/router';
import { Form, Input, TextField, Label, Button, Errors, ActivityIndicator, FieldDescription } from '@troon/ui';
import { Show, createEffect, Switch, Match } from 'solid-js';
import { clientAction } from '@troon/api-client';
import { useTrackEvent } from '@troon/analytics';
import { gql, mutationAction, useMutation } from '../../../graphql';
import { useAuthStore } from '../flow';
import type { AuthCompleteCredentials } from '../auth';

type Props = {
	onComplete?: (credentials: AuthCompleteCredentials) => void;
};

export function Login(props: Props) {
	const [store, setStore] = useAuthStore();
	const location = useLocation();
	const trackEvent = useTrackEvent();

	const action = clientAction(
		'post',
		'/v0/auth/login',
		{},
		{
			onSuccess: async (res) => {
				trackEvent('login', { email: res.user.email });
				props.onComplete?.({
					accessToken: res.accessToken,
					refreshToken: res.refreshToken,
					user: res.user,
				});
			},
			revalidate: undefined,
		},
	);
	const magic = useMutation(magicLink);
	const magicSubmision = useSubmission(magic);

	const handleReset = useAction(resetPassword);
	const resetSubmission = useSubmission(resetPassword);

	createEffect(() => {
		if (resetSubmission.pending) {
			trackEvent('passwordResetCode');
		}
		if (resetSubmission.result) {
			setStore({ flow: 'forgotPassword', step: '/auth/reset' });
			resetSubmission.clear();
		}
	});

	createEffect(() => {
		if (magicSubmision.result) {
			setStore({ flow: 'magicLink', step: '/auth/magic-link' });
			magicSubmision.clear();
		}
	});

	return (
		<Switch>
			<Match when={!store.data.email}>
				<Navigate href={`/auth${location.search}`} />
			</Match>
			<Match when={true}>
				<Form action={action} method="post" suppressRequired>
					<input name="email" type="hidden" value={store.data.email} />

					<TextField name="password">
						<Label>Password</Label>
						<Input type="password" value={store.data.password} autofocus />
						<FieldDescription>
							<p class="flex flex-row items-center text-sm">
								Forgot your password?{' '}
								<Button
									class="inline-flex max-w-min shrink p-px font-normal text-nowrap normal-case"
									appearance="transparent"
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										const data = new FormData();
										data.set('emailOrRewardsId', store.data.email!);
										data.set('redirectUrl', store.redirect);
										handleReset(data);
									}}
								>
									Reset it now
								</Button>
								.
								<Show when={resetSubmission.pending}>
									<ActivityIndicator class="ms-2 size-4 text-brand" />
								</Show>
							</p>
						</FieldDescription>
					</TextField>

					<Errors />

					<Button disabled={resetSubmission.pending} type="submit">
						Log in
					</Button>
				</Form>

				<p class="my-6 flex items-center justify-stretch gap-4">
					<span class="h-px shrink-0 grow bg-neutral" />
					<span class="shrink grow-0">or</span>
					<span class="h-px shrink-0 grow bg-neutral" />
				</p>

				<Form action={magic} document={magicLinkMutation}>
					<input name="email" type="hidden" value={store.data.email} />
					<input name="redirect" type="hidden" value={store.redirect} />
					<Button type="submit" appearance="secondary">
						Log in without password
					</Button>
					<p class="text-center text-sm">We’ll send you an email to log in instantly.</p>
				</Form>
			</Match>
		</Switch>
	);
}

const resetPassword = clientAction('post', '/v0/auth/forgot-password');

const magicLinkMutation = gql(`
mutation emailMagicLink($email: String!, $redirect: String) {
	emailMagicLink(email: $email, redirectUrl: $redirect) {
		ok
	}
}`);

const magicLink = mutationAction(magicLinkMutation, {
	track: {
		event: 'emailMagicLink',
	},
});
